import type { Currency } from "@loot-os/api-contract";
import type { RefCallback } from "react";

import type {
  BoxCardContent,
  RankedOpen,
  TemplateAction,
  TemplateLink,
} from "../shared/content.js";

/** The catalog's tabs and sorts, as the app's URL search names them. */
export const BOX_TABS = ["all", "new", "favorites"] as const;
export type BoxTab = (typeof BOX_TABS)[number];

export const BOX_SORTS = ["default", "price-asc", "price-desc", "popular"] as const;
export type BoxSort = (typeof BOX_SORTS)[number];

/** Every section of the boxes page and the content it renders, the same under every template. */
export interface BoxesContent {
  breadcrumb: BoxesBreadcrumbContent;
  title: BoxesTitleContent;
  tabs: BoxTabsContent;
  /** The store's box categories as a row of chips; left out when it offers none. */
  categories?: BoxCategoriesContent;
  filters: BoxFiltersContent;
  topHits: TopHitsContent;
  grid: BoxGridContent;
}

export type BoxesSectionKey = keyof BoxesContent;

export interface BoxesBreadcrumbContent {
  /** The pages above this one, outermost first. */
  trail: readonly (TemplateLink & { label: string })[];
  current: string;
}

export interface BoxesTitleContent {
  text: string;
}

export interface BoxTabsContent {
  /** The tab showing. */
  value: BoxTab;
  /** The tab list's accessible name. */
  label: string;
  options: readonly { value: BoxTab; label: string }[];
  onValueChange: (tab: BoxTab) => void;
}

/** The box categories the grid can narrow to, one at a time, beside whichever tab is showing. */
export interface BoxCategoriesContent {
  /** The option showing; `"all"` for every box. */
  value: string;
  /** The chip group's accessible name. */
  label: string;
  /** Every box first (`"all"`), then the store's categories in its order. */
  options: readonly { value: string; label: string }[];
  onValueChange: (value: string) => void;
}

export interface TextField {
  value: string;
  /** The field's accessible name. */
  label: string;
  placeholder: string;
  onChange: (text: string) => void;
}

export interface AmountField extends TextField {
  /** What was typed does not read as an amount, so it filters nothing. */
  invalid: boolean;
}

export interface BoxFiltersContent {
  /** `focusRef` goes on the search input, which the header's search button focuses. */
  search: TextField & { focusRef?: RefCallback<HTMLInputElement> };
  price: { currency: Currency; min: AmountField; max: AmountField };
  sort: {
    value: BoxSort;
    label: string;
    options: readonly { value: BoxSort; label: string }[];
    onValueChange: (sort: BoxSort) => void;
  };
  /** Only boxes the shopper's balance covers; null signed out, with no balance to compare. */
  affordable: {
    checked: boolean;
    label: string;
    onCheckedChange: (checked: boolean) => void;
  } | null;
  clear: { label: string; onClear: () => void };
  /** How many filters are set: Clear shows while any is, and a phone's Filters button counts them. */
  activeCount: number;
  /**
   * On a phone the filters wait in a sheet behind a floating button: its label, its accessible
   * name for `activeCount` filters set (the label first, WCAG 2.5.3), the sheet's heading, and
   * its close.
   */
  sheet: {
    label: string;
    countLabel: (count: number) => string;
    title: string;
    doneLabel: string;
  };
}

export interface TopHitsContent {
  title: string;
  /** Most valuable first. `[]` renders nothing, and is all a signed-out visitor gets. */
  opens: readonly RankedOpen[];
}

export type BoxGridState =
  | { status: "loading" }
  | { status: "error"; message: string }
  /** Nothing to show, and why; `action` is what would change that, when something would. */
  | { status: "empty"; message: string; action: TemplateAction | null }
  | { status: "ready"; cards: readonly BoxCardContent[] };

export interface BoxGridContent {
  title: string;
  currency: Currency;
  state: BoxGridState;
}
